import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SharedLink, CreateSharedLinkData, UpdateSharedLinkData } from '../models/shared-link.model';
import { useToast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';

export const useSharedLinks = () => {
  const [links, setLinks] = useState<SharedLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { t } = useI18n();
  const { user } = useAuth();

  const fetchLinks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: rpcError } = await supabase.rpc('get_shared_links');
      
      if (rpcError) {
        console.error('[useSharedLinks] Error fetching links:', rpcError);
        setError(rpcError.message);
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: rpcError.message,
        });
        return;
      }
      
      setLinks(data || []);
    } catch (err: any) {
      console.error('[useSharedLinks] Unexpected error:', err);
      setError(err.message);
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: err.message,
      });
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 200));
      setLoading(false);
    }
  };

  const createLink = async (data: CreateSharedLinkData): Promise<boolean> => {
    try {
      if (!user?.id) {
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: 'User not authenticated',
        });
        return false;
      }

      // Generate a unique token (UUID v4)
      const token = crypto.randomUUID();
      
      const { error: insertError } = await supabase
        .from('shared_itineraries')
        .insert({
          itinerary_id: data.itinerary_id,
          share_token: token,
          password: data.password || null,
          expires_at: data.expires_at || null,
          created_by_user_id: user.id,
        });
      
      if (insertError) {
        console.error('[useSharedLinks] Error creating link:', insertError);
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: insertError.message,
        });
        return false;
      }
      
      toast({
        title: t('common.success'),
        description: t('shared.linkCreated'),
      });
      
      await fetchLinks();
      return true;
    } catch (err: any) {
      console.error('[useSharedLinks] Unexpected error:', err);
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: err.message,
      });
      return false;
    }
  };

  const updateLink = async (id: string, data: UpdateSharedLinkData): Promise<boolean> => {
    try {
      const { error: rpcError } = await supabase.rpc('update_shared_link' as any, {
        p_id: id,
        p_password: data.password,
        p_expires_at: data.expires_at,
      });

      if (rpcError) {
        console.error('[useSharedLinks] Error updating link:', rpcError);
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: rpcError.message,
        });
        return false;
      }

      toast({
        title: t('common.success'),
        description: t('shared.linkUpdated'),
      });

      await fetchLinks();
      return true;
    } catch (err: any) {
      console.error('[useSharedLinks] Unexpected error:', err);
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: err.message,
      });
      return false;
    }
  };

  const deleteLink = async (id: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('shared_itineraries')
        .delete()
        .eq('id', id);
      
      if (deleteError) {
        console.error('[useSharedLinks] Error deleting link:', deleteError);
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: deleteError.message,
        });
        return false;
      }
      
      toast({
        title: t('common.success'),
        description: t('shared.linkDeleted'),
      });
      
      await fetchLinks();
      return true;
    } catch (err: any) {
      console.error('[useSharedLinks] Unexpected error:', err);
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: err.message,
      });
      return false;
    }
  };

  useEffect(() => {
    fetchLinks();
  }, []);

  return {
    links,
    loading,
    error,
    fetchLinks,
    createLink,
    updateLink,
    deleteLink,
  };
};
